import { Component, OnDestroy, OnInit, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService, OfferingMediaItem } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-catalog-offering-media-manager',
  imports: [FormsModule, ButtonModule, InputTextModule],
  template: `
    <section class="media-manager">
      <div class="toolbar">
        <label class="upload-field">
          <span>{{ t('catalog.offerings.media.uploadImage') }}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" (change)="onFileSelected($event)" />
        </label>
        <label class="alt-field">
          <span>{{ t('catalog.offerings.media.altText') }}</span>
          <input pInputText [(ngModel)]="newAltText" />
        </label>
        <button pButton type="button" [label]="t('catalog.offerings.media.addImage')" [disabled]="saving() || !selectedFile()" (click)="uploadMedia()"></button>
      </div>

      <details class="file-id-fallback">
        <summary>{{ t('catalog.offerings.media.advancedFileId') }}</summary>
        <div class="toolbar">
          <label class="file-id-field">
            <span>{{ t('catalog.offerings.media.fileId') }}</span>
            <input pInputText [(ngModel)]="newFileId" [placeholder]="t('catalog.offerings.media.fileIdPlaceholder')" />
          </label>
          <button pButton type="button" text="true" [label]="t('catalog.offerings.media.attachByFileId')" [disabled]="saving() || !offeringId()" (click)="addMedia()"></button>
        </div>
      </details>

      @if (loading()) {
        <p class="muted">{{ t('common.loading') }}</p>
      } @else if (items().length === 0) {
        <p class="muted">{{ t('catalog.offerings.media.noImages') }}</p>
      } @else {
        <ul class="media-list">
          @for (item of items(); track item.id; let index = $index) {
            <li>
              <div class="preview">
                <img [src]="previewUrl(item.id)" [alt]="item.altText || t('catalog.offerings.media.image')" loading="lazy" />
                @if (item.isPrimary) {
                  <span class="primary-badge">{{ t('catalog.offerings.media.primaryImage') }}</span>
                }
              </div>
              <div class="meta">
                <label>
                  <span>{{ t('catalog.offerings.media.altText') }}</span>
                  <input pInputText [(ngModel)]="item.altText" (blur)="saveAltText(item)" />
                </label>
                <p class="file-id">{{ t('catalog.offerings.media.fileId') }}: {{ item.fileId }}</p>
              </div>
              <div class="actions">
                <button pButton type="button" text="true" size="small" [label]="t('catalog.offerings.media.moveUp')" [disabled]="index === 0 || saving()" (click)="moveItem(index, -1)"></button>
                <button pButton type="button" text="true" size="small" [label]="t('catalog.offerings.media.moveDown')" [disabled]="index === items().length - 1 || saving()" (click)="moveItem(index, 1)"></button>
                <button pButton type="button" text="true" size="small" severity="danger" [label]="t('catalog.offerings.media.removeImage')" [disabled]="saving()" (click)="removeMedia(item.id)"></button>
              </div>
            </li>
          }
        </ul>
      }

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
      @if (message()) {
        <p class="success">{{ message() }}</p>
      }
    </section>
  `,
  styles: [`
    .media-manager { display: grid; gap: 12px; margin-top: 10px; }
    .toolbar { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); align-items: end; }
    .toolbar label { display: grid; gap: 6px; }
    .toolbar span { font-size: 12px; color: #475569; font-weight: 600; }
    .file-id-fallback { border: 1px dashed #cbd5e1; border-radius: 8px; padding: 8px 10px; }
    .file-id-fallback summary { cursor: pointer; color: #64748b; font-size: 12px; font-weight: 600; }
    .media-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
    li {
      display: grid;
      gap: 10px;
      grid-template-columns: 120px 1fr auto;
      align-items: center;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 10px;
    }
    .preview { position: relative; width: 120px; height: 90px; border-radius: 8px; overflow: hidden; background: #f8fafc; }
    .preview img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .primary-badge {
      position: absolute;
      inset-inline-start: 6px;
      top: 6px;
      background: #1d4ed8;
      color: white;
      font-size: 10px;
      font-weight: 700;
      border-radius: 999px;
      padding: 2px 8px;
    }
    .meta { display: grid; gap: 6px; }
    .meta label { display: grid; gap: 4px; }
    .meta span { font-size: 12px; color: #475569; font-weight: 600; }
    .file-id { margin: 0; font-size: 12px; color: #64748b; word-break: break-all; }
    .actions { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; }
    .muted { margin: 0; color: #64748b; }
    .error { margin: 0; color: #b91c1c; }
    .success { margin: 0; color: #166534; }
    @media (max-width: 768px) {
      li { grid-template-columns: 1fr; }
      .preview { width: 100%; height: 160px; }
      .actions { flex-direction: row; flex-wrap: wrap; justify-content: flex-start; }
    }
  `]
})
export class CatalogOfferingMediaManagerComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly offeringId = input.required<string>();
  readonly changed = output<OfferingMediaItem[]>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly items = signal<OfferingMediaItem[]>([]);
  readonly selectedFile = signal<File | null>(null);
  readonly previewUrls = signal<Record<string, string>>({});

  newFileId = '';
  newAltText = '';

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.revokePreviewUrls();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  previewUrl(mediaId: string): string {
    return this.previewUrls()[mediaId] ?? '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile.set(file);
    this.error.set('');
  }

  load(): void {
    const offeringId = this.offeringId();
    if (!offeringId) {
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.service.listOfferingMedia(offeringId).subscribe({
      next: items => {
        const sorted = this.sortItems(items);
        this.items.set(sorted);
        this.changed.emit(sorted);
        this.refreshPreviews(sorted);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.offerings.media.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  uploadMedia(): void {
    const offeringId = this.offeringId();
    const file = this.selectedFile();
    if (!offeringId || !file) {
      this.error.set(this.t('catalog.offerings.media.uploadRequired'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    this.service.uploadOfferingMedia(offeringId, file, this.newAltText.trim()).subscribe({
      next: () => {
        this.selectedFile.set(null);
        this.newAltText = '';
        this.message.set(this.t('catalog.offerings.media.saved'));
        this.saving.set(false);
        this.load();
      },
      error: err => {
        this.error.set(this.resolveUploadError(err));
        this.saving.set(false);
      }
    });
  }

  addMedia(): void {
    const offeringId = this.offeringId();
    const fileId = this.newFileId.trim();
    if (!offeringId || !fileId) {
      this.error.set(this.t('catalog.offerings.media.invalidMedia'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    this.service.addOfferingMedia(offeringId, { fileId, altText: this.newAltText.trim() }).subscribe({
      next: () => {
        this.newFileId = '';
        this.newAltText = '';
        this.message.set(this.t('catalog.offerings.media.saved'));
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.error.set(this.t('catalog.offerings.media.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  saveAltText(item: OfferingMediaItem): void {
    const offeringId = this.offeringId();
    if (!offeringId) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    this.service.updateOfferingMedia(offeringId, item.id, { altText: item.altText.trim() }).subscribe({
      next: updated => {
        this.items.update(items => items.map(row => row.id === updated.id ? updated : row));
        this.changed.emit(this.items());
        this.message.set(this.t('catalog.offerings.media.saved'));
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.offerings.media.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  moveItem(index: number, direction: -1 | 1): void {
    const offeringId = this.offeringId();
    const nextIndex = index + direction;
    const current = [...this.items()];
    if (!offeringId || nextIndex < 0 || nextIndex >= current.length) {
      return;
    }

    [current[index], current[nextIndex]] = [current[nextIndex], current[index]];
    const mediaIds = current.map(item => item.id);

    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    this.service.reorderOfferingMedia(offeringId, { mediaIds }).subscribe({
      next: items => {
        const sorted = this.sortItems(items);
        this.items.set(sorted);
        this.changed.emit(sorted);
        this.message.set(this.t('catalog.offerings.media.reordered'));
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.offerings.media.reorderFailed'));
        this.saving.set(false);
      }
    });
  }

  removeMedia(mediaId: string): void {
    const offeringId = this.offeringId();
    if (!offeringId) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    this.service.deleteOfferingMedia(offeringId, mediaId).subscribe({
      next: () => {
        this.message.set(this.t('catalog.offerings.media.deleted'));
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.error.set(this.t('catalog.offerings.media.deleteFailed'));
        this.saving.set(false);
      }
    });
  }

  private refreshPreviews(items: OfferingMediaItem[]): void {
    this.revokePreviewUrls();
    this.previewUrls.set({});

    for (const item of items) {
      const source = item.thumbnailUrl || item.imageUrl;
      if (!source) {
        continue;
      }

      this.service.fetchMediaContent(source).subscribe({
        next: blob => {
          const objectUrl = URL.createObjectURL(blob);
          this.previewUrls.update(urls => ({ ...urls, [item.id]: objectUrl }));
        },
        error: () => {
          this.error.set(this.t('catalog.offerings.media.previewFailed'));
        }
      });
    }
  }

  private revokePreviewUrls(): void {
    for (const objectUrl of Object.values(this.previewUrls())) {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private resolveUploadError(error: unknown): string {
    const code = (error as { error?: { error?: { code?: string } } })?.error?.error?.code;
    switch (code) {
      case 'invalid_content_type':
        return this.t('catalog.offerings.media.invalidContentType');
      case 'invalid_image':
        return this.t('catalog.offerings.media.invalidImage');
      case 'image_dimensions_exceeded':
        return this.t('catalog.offerings.media.imageDimensionsExceeded');
      case 'file_too_large':
        return this.t('catalog.offerings.media.fileTooLarge');
      case 'file_required':
        return this.t('catalog.offerings.media.uploadRequired');
      default:
        return this.t('catalog.offerings.media.uploadFailed');
    }
  }

  private sortItems(items: OfferingMediaItem[]): OfferingMediaItem[] {
    return [...items].sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id));
  }
}
